import { LazyExoticComponent, Suspense, useEffect, useState } from "react";
import { initAmplitude, trackPageView } from "@/core/amplitude";
import { useAuth } from "@/store/auth";
import { lazyWithRetry as lazy } from "@/utils/lazy-with-retry";
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";

import { initializeDependencies } from "@/config/boot";
import PrivateRoute from "@/components/app/PrivateRoute";
import PublicRoute from "@/components/app/PublicRoute";
import { Spinner } from "@/components/shared/Spinner/Spinner";

import { RoutePermission } from "./components/app/RoutePermission";
import {
  ANALYTICS_WIDGET_FEATURE_KEY,
  isFeatureEnabled,
} from "./features/chat-agent/feature-check";
import { Toaster } from "./components/ui/sonner";
import { isLocal } from "./constants/env";
import { subscriptionEnabled } from "./constants/subscriptions";
import {
  PERMISSION_PAYMENTS_CREATE,
  PERMISSION_PAYMENTS_EDIT,
  PERMISSION_PAYMENTS_LIST,
  PERMISSION_PAYMENTS_VIEW,
  PERMISSION_CLIENTS_FOLLOW_UP_LIST,
  PERMISSION_PRODUCTS_LIST,
  PERMISSION_PRODUCTS_CREATE,
  PERMISSION_PRODUCTS_VIEW,
  PERMISSION_PRODUCTS_EDIT,
  PERMISSION_ORDER_SCHEDULED_DELIVERIES_LIST,
} from "./utils/permissions";
import { useSettings } from "./store/settings";

const DashboardLayout = lazy(() => import("./layouts/dashboard/DashboardLayout"));
const Account = lazy(() => import("@/pages/SupplierProfile"));
const SubscriptionPage = lazy(() => import("./pages/Subscription/SubscriptionPage"));
const CreditsPage = lazy(() => import("./pages/Credits/CreditsPage"));
const TransactionsPage = lazy(() => import("./pages/Credits/TransactionsPage"));
const CreditExtractPage = lazy(() => import("./pages/Credits/CreditExtractPage"));
const SegmentsPage = lazy(() => import("./pages/Segments/SegmentsPage"));
const OffersPage = lazy(() => import("./pages/Offers/Offers"));
const CreateOfferPage = lazy(() => import("./pages/Offers/CreateOffer"));
const ShowOfferPage = lazy(() => import("./pages/Offers/ShowOffer"));
const ClientShow = lazy(() => import("./pages/Clients/ClientShow"));
const SupplierEventsPage = lazy(() => import("./pages/Clients/SupplierEvents"));
const CoinsVaultPage = lazy(() => import("./pages/Profile/CoinsVault"));
const AcquisitionsListPage = lazy(() => import("./pages/Acquisitions/AcquisitionsList"));
const PurchasesOrderCreatePage = lazy(() => import("./pages/Purchases/PurchaseOrder"));
const PurchasesShowPage = lazy(() => import("./pages/Purchases/PurchasesShow"));
const QuotesListPage = lazy(() => import("./pages/Quotes/QuotesList"));
const QuoteCreationPage = lazy(() => import("./pages/Quotes/QuoteCreation"));
const PurchasesListPage = lazy(() => import("./pages/Purchases/PurchasesList"));
const PurchasesCreatePage = lazy(() => import("./pages/Purchases/PurchasesCreate"));
const InvoicePage = lazy(() => import("./pages/Invoices/InvoicePage"));
const TicketsList = lazy(() => import("./pages/Tickets/TicketsList"));
const TicketDetailPage = lazy(() => import("./pages/Tickets/TicketDetail"));
const TicketTypeCreatePage = lazy(() => import("./pages/Tickets/TicketTypeCreate"));
const TicketCreatePage = lazy(() => import("./pages/Tickets/TicketCreate"));
const IntegrationsPage = lazy(() => import("./pages/Integrations/IntegrationsPage"));
const IntegrationsCallbackPage = lazy(
  () => import("./pages/Integrations/IntegrationsCallbackPage")
);

const PaymentDetailPage = lazy(() => import("./pages/Payments/PaymentDetailPage"));
const CommercialsList = lazy(() => import("./pages/Commercials/CommercialsList"));
const BulkLoadsPage = lazy(() => import("./pages/bulk-loads/BulkLoadsPage"));
const NewBulkLoadPage = lazy(() => import("./pages/bulk-loads/NewBulkLoadPage"));
const BulkLoadPage = lazy(() => import("./pages/bulk-loads/BulkLoadPage"));
const RoutesGateList = lazy(async () => {
  const mod = await import("./pages/Routes/RoutesGate");
  return { default: () => <mod.default fallback="list" /> };
});
const RoutesGateCreate = lazy(async () => {
  const mod = await import("./pages/Routes/RoutesGate");
  return { default: () => <mod.default fallback="create" /> };
});
const RoutesGateEdit = lazy(async () => {
  const mod = await import("./pages/Routes/RoutesGate");
  return { default: () => <mod.default fallback="edit" /> };
});
const RoutesWorkspacePage = lazy(async () => {
  const mod = await import("./pages/Routes/RoutesWorkspace");
  return { default: () => <mod.default /> };
});
const RoutesAgendaPage = lazy(() => import("./pages/Routes/RoutesAgenda"));
const DispatchPage = lazy(() => import("./pages/Dispatch/DispatchPage"));
const DispatchDetailPage = lazy(() => import("./pages/Dispatch/DispatchDetailPage"));
const Restricted = lazy(() => import("./pages/Restricted/Restricted"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));
const EmailLinkLogin = lazy(() => import("./pages/EmailLinkLogin"));
const Login = lazy(() => import("./pages/Login/Login"));
const SignupPage = lazy(() => import("./pages/Signup/SignupPage"));
const PasswordResetPage = lazy(() => import("./pages/PasswordResetPage"));

const ClientsPage = lazy(() => import("./pages/Clients/ClientsPage"));
const ContactsListPage = lazy(() => import("./pages/Clients/ContactsListPage"));
const CategoriesPage = lazy(() => import("./pages/Categories/CategoriesPage"));
const BrandsPage = lazy(() => import("./pages/Brands/BrandsPage"));
const PaymentsPage = lazy(() => import("@/pages/Payments/Payments"));
const ChannelsPage = lazy(() => import("./pages/Channels/ChannelsPage"));
const PipelinesPage = lazy(() => import("./pages/Pipelines/PipelinesPage"));
const CreatePayment = lazy(() => import("./pages/Payments/CreatePayment"));
const PaymentsDetailPage = lazy(() => import("./pages/Payments/PaymentsDetailPage"));
const PaymentEditPage = lazy(() => import("./pages/Payments/PaymentEditPage"));
const BannersPage = lazy(() => import("./pages/Banners/BannersPage"));

const SalesPage = lazy(() => import("./pages/Sales/SalesPage"));
const ScheduledDeliveriesPage = lazy(() => import("./pages/Orders/ScheduledDeliveriesPage"));
const SuperAgentPage = lazy(() => import("./pages/SuperAgent/SuperAgentPage"));

type LazyComponent = LazyExoticComponent<() => JSX.Element | null>;

// Component to track route changes
function RouteTracker() {
  const location = useLocation();
  const { commercial, user } = useAuth();

  useEffect(() => {
    trackPageView(location.pathname, undefined, commercial, user);
  }, [location, commercial, user]);

  return null;
}

function DefaultRedirect() {
  const { user } = useAuth();
  const widgetEnabled = isFeatureEnabled(user?.features, ANALYTICS_WIDGET_FEATURE_KEY);
  return <Navigate replace to={widgetEnabled ? "/super-agent" : "/purchases"} />;
}

function SuperAgentRouteGate() {
  const { user } = useAuth();
  if (!isFeatureEnabled(user?.features, ANALYTICS_WIDGET_FEATURE_KEY)) {
    return <Navigate replace to="/purchases" />;
  }
  return (
    <Suspense>
      <SuperAgentPage />
    </Suspense>
  );
}

function App() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const { hasLegacySubOrders } = useSettings();

  useEffect(() => {
    initializeDependencies()
      .then(() => {
        initAmplitude();
        setReady(true);
      })
      .catch(() => setError("Error de configuración remota."));
  }, []);

  if (error) return <>{error}</>;
  if (!ready) return <Spinner />;

  const lazyRoute = (Component: LazyComponent, permissions: string[] = []) => (
    <RoutePermission
      permissions={permissions}
      element={
        <Suspense>
          <Component />
        </Suspense>
      }
    />
  );

  return (
    <>
      <Toaster richColors position="top-right" />
      <Router>
        <RouteTracker />
        <Routes>
          <Route path="/" element={<PrivateRoute />}>
            <Route
              path="/"
              element={
                <DashboardLayout>
                  <Outlet />
                </DashboardLayout>
              }
            >
              <Route path="/" element={<DefaultRedirect />} />
              <Route path="/super-agent" element={<SuperAgentRouteGate />} />
              <Route path="/account" element={lazyRoute(Account, ["route.account.show"])} />
              <Route
                path="/tania/documents"
                element={lazyRoute(lazy(() => import("./pages/TaniaDocuments/DocumentsPage")))}
              />
              <Route
                path="/tania/configurations"
                element={<Navigate replace to="/tania/agents" />}
              />
              <Route
                path="/tania/agents"
                element={<Navigate replace to="/tania/agents/agents" />}
              />
              <Route
                path="/tania/agents/:tab"
                element={lazyRoute(lazy(() => import("./pages/TaniaAgents/TaniaAgentsPage")))}
              />
              <Route
                path="/tania/agents/integrations/:integrationId"
                element={lazyRoute(lazy(() => import("./pages/TaniaAgents/TaniaAgentsPage")))}
              />
              <Route
                path="/tania/metrics"
                element={lazyRoute(
                  lazy(() => import("./pages/Metrics/TaniaMetricsPage")),
                  ["route.metrics.list"]
                )}
              />
              {subscriptionEnabled && (
                <Route
                  path="/subscription"
                  element={lazyRoute(SubscriptionPage, ["route.subscription.show"])}
                />
              )}
              <Route
                path="/customize"
                element={lazyRoute(lazy(() => import("./pages/Customize/CustomizePage")))}
              />
              <Route path="/banners" element={lazyRoute(BannersPage)} />
              <Route
                path="/metrics"
                element={lazyRoute(lazy(() => import("./pages/PowerBIReport")))}
              />
              <Route
                path="/metrics/dashboard"
                element={lazyRoute(lazy(() => import("./pages/Metrics/DistrifyMetricsPage")))}
              />
              <Route
                path="/clients/follow-up"
                element={lazyRoute(
                  lazy(() => import("./pages/Commercials/CommercialsTracking")),
                  [PERMISSION_CLIENTS_FOLLOW_UP_LIST]
                )}
              />
              <Route
                path="/help"
                element={lazyRoute(
                  lazy(() => import("./pages/Help/Help")),
                  ["route.help.show"]
                )}
              />
              <Route
                path="/orders"
                element={lazyRoute(
                  hasLegacySubOrders ? lazy(() => import("./pages/Orders/Orders")) : lazy(() => import("./pages/Dispatch/DispatchPage")),
                  ["route.orders.list"]
                )}
              />
              <Route
                path="/orders/:id"
                element={lazyRoute(
                  lazy(() => import("./pages/Orders/OrderDetail")),
                  ["route.orders.show"]
                )}
              />
              <Route
                path="/orders/:id/invoice"
                element={lazyRoute(
                  lazy(() => import("./pages/Invoices/InvoiceOrderPage")),
                  ["route.orders.show"]
                )}
              />
              <Route
                path="/products"
                element={lazyRoute(
                  lazy(() => import("./pages/ProductManagement/ProductsPage")),
                  [PERMISSION_PRODUCTS_LIST]
                )}
              />
              <Route
                path="/products/new"
                element={lazyRoute(
                  lazy(() => import("./pages/ProductManagement/NewProductPage")),
                  [PERMISSION_PRODUCTS_CREATE]
                )}
              />
              <Route
                path="/products/:id"
                element={lazyRoute(
                  lazy(() => import("./pages/ProductManagement/ManageProductPage")),
                  [PERMISSION_PRODUCTS_VIEW]
                )}
              />
              <Route
                path="/products/create/new"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/CreateProductVariant")),
                  [PERMISSION_PRODUCTS_CREATE]
                )}
              />
              <Route
                path="/products/:id/old"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/VariantDetailPage")),
                  [PERMISSION_PRODUCTS_VIEW]
                )}
              />
              <Route
                path="/products/:id/edit"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/EditProduct")),
                  [PERMISSION_PRODUCTS_EDIT]
                )}
              />
              <Route
                path="/curves"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/CurvesPage")),
                  [PERMISSION_PRODUCTS_LIST]
                )}
              />
              <Route
                path="/splits"
                element={lazyRoute(
                  lazy(() => import("./pages/Products/Splits")),
                  [PERMISSION_PRODUCTS_LIST]
                )}
              />
              <Route
                path="/categories"
                element={lazyRoute(CategoriesPage, ["route.categories.list"])}
              />
              <Route path="/brands" element={lazyRoute(BrandsPage, ["route.brands.list"])} />
              <Route path="/payments" element={lazyRoute(PaymentsPage, [PERMISSION_PAYMENTS_LIST])} />
              <Route
                path="/payments/create/new"
                element={lazyRoute(CreatePayment, [PERMISSION_PAYMENTS_CREATE])}
              />
              <Route
                path="/payments/:id/edit"
                element={lazyRoute(PaymentEditPage, [PERMISSION_PAYMENTS_EDIT])}
              />
              <Route
                path="/payment/:id"
                element={lazyRoute(PaymentDetailPage, [PERMISSION_PAYMENTS_VIEW])}
              />
              <Route path="/clients" element={lazyRoute(ClientsPage, ["route.clients.list"])} />
              <Route
                path="/clients/contacts"
                element={lazyRoute(ContactsListPage, ["route.clients.list"])}
              />
              <Route
                path="/marketing/lists"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Lists/ClientListsPage")),
                  ["route.clients.list"]
                )}
              />
              <Route
                path="/marketing/lists/:id"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Lists/ClientListShowPage")),
                  ["route.clients.list"]
                )}
              />
              <Route
                path="/marketing/lists/:id/add-clients"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Lists/AddClientsToListPage")),
                  ["route.clients.list"]
                )}
              />
              <Route
                path="/marketing/campaigns"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Campaigns/CampaignsPage")),
                  ["route.clients.list"]
                )}
              />
              <Route
                path="/marketing/campaigns/create"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Campaigns/CreateCampaignPage")),
                  ["route.clients.list"]
                )}
              />
              <Route
                path="/marketing/campaigns/:id"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Campaigns/CampaignShowPage")),
                  ["route.clients.list"]
                )}
              />
              <Route
                path="/marketing/campaigns/:id/edit"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Campaigns/EditCampaignPage")),
                  ["route.clients.list"]
                )}
              />
              <Route
                path="/marketing/campaigns/:id/executions"
                element={lazyRoute(
                  lazy(() => import("./pages/Marketing/Campaigns/CampaignExecutionsPage")),
                  ["route.clients.list"]
                )}
              />
              <Route path="/credits" element={lazyRoute(CreditsPage, ["route.credits.list"])} />
              <Route
                path="/credits/:id/transactions"
                element={lazyRoute(TransactionsPage, ["route.transactions.list"])}
              />
              <Route
                path="/credits/:id/transactions/extract"
                element={lazyRoute(CreditExtractPage, ["route.transactions.list"])}
              />
              <Route path="/segments" element={lazyRoute(SegmentsPage, ["route.clients.list"])} />
              <Route path="/offers" element={lazyRoute(OffersPage, ["route.offers.list"])} />
              <Route
                path="/offers/create"
                element={lazyRoute(CreateOfferPage, ["route.offers.create"])}
              />
              <Route
                path="/offers/:id/show"
                element={lazyRoute(ShowOfferPage, ["route.offers.show"])}
              />
              <Route
                path="/clients/:id/show"
                element={lazyRoute(ClientShow, ["route.clients.show"])}
              />
              <Route
                path="/clients/events"
                element={lazyRoute(SupplierEventsPage, ["route.clients.show"])}
              />
              <Route
                path="/clients/:id/coins"
                element={lazyRoute(CoinsVaultPage, ["route.clients.show"])}
              />
              <Route
                path="/acquisitions"
                element={lazyRoute(AcquisitionsListPage, ["route.purchases.my-purchases"])}
              />
              <Route
                path="/acquisitions/create"
                element={lazyRoute(PurchasesOrderCreatePage, ["route.purchases.my-purchases"])}
              />
              <Route
                path="/acquisitions/:id/show"
                element={lazyRoute(PurchasesShowPage, ["route.purchases.my-purchases"])}
              />
              <Route path="/quotes" element={lazyRoute(QuotesListPage, ["route.quotes.list"])} />
              <Route
                path="/quotes/create"
                element={lazyRoute(QuoteCreationPage, ["action.quotes.create"])}
              />
              <Route
                path="/quotes/:id/show"
                element={lazyRoute(PurchasesShowPage, ["route.purchases.show"])}
              />
              <Route
                path="/purchases"
                element={lazyRoute(PurchasesListPage, ["route.purchases.list"])}
              />
              <Route path="/sales" element={lazyRoute(SalesPage, ["orders.list"])} />
              <Route
                path="/purchases/create"
                element={lazyRoute(PurchasesCreatePage, ["route.purchases.create"])}
              />
              <Route
                path="/purchases/scheduled-deliveries"
                element={lazyRoute(ScheduledDeliveriesPage, [PERMISSION_ORDER_SCHEDULED_DELIVERIES_LIST])}
              />
              <Route
                path="/purchases/:id/show"
                element={lazyRoute(PurchasesShowPage, ["route.purchases.show"])}
              />
              <Route
                path="/purchases/:id/invoice"
                element={lazyRoute(InvoicePage, ["route.purchases.show"])}
              />
              <Route path="/tickets" element={lazyRoute(TicketsList, ["route.tickets.list"])} />
              <Route
                path="/tickets/:id"
                element={lazyRoute(TicketDetailPage, ["route.tickets.list"])}
              />
              <Route
                path="/tickets/create/:type"
                element={lazyRoute(TicketTypeCreatePage, ["route.tickets.list"])}
              />
              <Route
                path="/tickets/create/:type/:purchaseId"
                element={lazyRoute(TicketCreatePage, ["route.tickets.list"])}
              />
              <Route
                path="/integrations"
                element={lazyRoute(IntegrationsPage, ["route.integrations"])}
              />
              <Route
                path="/integrations/:id/callback"
                element={lazyRoute(IntegrationsCallbackPage)}
              />
              <Route
                path="/payments/:id"
                element={lazyRoute(PaymentsDetailPage, ["route.payments.show"])}
              />
              <Route
                path="/commercials"
                element={lazyRoute(CommercialsList, ["route.users.list"])}
              />
              <Route
                path="/bulk-loads"
                element={lazyRoute(BulkLoadsPage, ["route.bulk-loads.list"])}
              />
              <Route
                path="/bulk-loads/new"
                element={lazyRoute(NewBulkLoadPage, ["route.bulk-loads.create"])}
              />
              <Route
                path="/bulk-loads/:id"
                element={lazyRoute(BulkLoadPage, ["route.bulk-loads.show"])}
              />
              <Route
                path="/dispatch-routes/new"
                element={lazyRoute(
                  lazy(() => import("./pages/DispatchRoutes/NewDispatchRoutePage"))
                )}
              />
              {/* TAN-954: la Agenda reemplaza la tabla como pantalla principal
                  de rutas; la gestión de rutas (tabla + CRUD) queda en /routes/list. */}
              <Route path="/routes" element={lazyRoute(RoutesAgendaPage, [])} />
              <Route path="/routes/list" element={lazyRoute(RoutesGateList, [])} />
              <Route
                path="/routes/create"
                element={lazyRoute(RoutesGateCreate, [])}
              />
              <Route
                path="/routes/execution"
                element={lazyRoute(RoutesWorkspacePage, [])}
              />
              <Route
                path="/routes/analytics"
                element={lazyRoute(RoutesWorkspacePage, [])}
              />
              <Route path="/routes/:id" element={lazyRoute(RoutesGateEdit, [])} />
              <Route path="/channels" element={lazyRoute(ChannelsPage, ["route.channels.list"])} />
              <Route
                path="/channels/instagram/callback"
                element={lazyRoute(lazy(() => import("./pages/Channels/InstagramCallbackPage")))}
              />
              <Route
                path="/pipelines"
                element={lazyRoute(PipelinesPage, ["route.pipelines.list"])}
              />
              <Route
                path="/customer-service/queue"
                element={lazyRoute(lazy(() => import("./pages/CustomerService/QueuePage")))}
              />
              <Route
                path="/customer-service/agents"
                element={lazyRoute(lazy(() => import("./pages/CustomerService/AgentsPage")))}
              />
              <Route
                path="/customer-service/conversations"
                element={lazyRoute(
                  lazy(() => import("./pages/CustomerService/ConversationsPageV2Route"))
                )}
              />
              <Route
                path="/customer-service/conversations/:contactId"
                element={lazyRoute(
                  lazy(() => import("./pages/CustomerService/ConversationsPageV2Route"))
                )}
              />
              <Route
                path="/loads"
                element={lazyRoute(
                  lazy(() => import("./pages/Loads/LoadsPage")),
                  ["route.loads.list"]
                )}
              />
              <Route
                path="/loads/new"
                element={lazyRoute(
                  lazy(() => import("./pages/Loads/CreateLoadPage")),
                  ["route.loads.list"]
                )}
              />
              <Route
                path="/loads/:id"
                element={lazyRoute(
                  lazy(() => import("./pages/Loads/ShowLoadPage")),
                  ["route.loads.list"]
                )}
              />
              <Route
                path="/loads/:id/edit"
                element={lazyRoute(
                  lazy(() => import("./pages/Loads/EditLoadPage")),
                  ["route.loads.list"]
                )}
              />
              <Route path="/dispatch" element={lazyRoute(DispatchPage)} />
              <Route path="/dispatch/:id" element={lazyRoute(DispatchDetailPage)} />
              <Route path="/restricted" element={<Restricted />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
          <Route path="/" element={<PublicRoute />}>
            <Route path="/link-login" element={lazyRoute(EmailLinkLogin)} />
            <Route path="/login" element={lazyRoute(Login)} />
            <Route path="/signup" element={lazyRoute(SignupPage)} />
            <Route path="/password-reset" element={lazyRoute(PasswordResetPage)} />
          </Route>
        </Routes>
      </Router>
    </>
  );
}

export default App;
